import React from 'react';
import { renderRichText } from 'gatsby-source-contentful/rich-text';
import { BLOCKS, INLINES, MARKS } from '@contentful/rich-text-types';

import Link from 'mui/components/elements/Link';

import BlockParagraph from './BlockParagraph';
import BlockH1 from './BlockH1';
import BlockH2 from './BlockH2';
import BlockH3 from './BlockH3';
import BlockH4 from './BlockH4';
import BlockH5 from './BlockH5';
import BlockH6 from './BlockH6';
import BlockTable from './BlockTable';
import BlockHr from './BlockHr';
import BlockList from './BlockList';
import BlockListItem from './BlockListItem';
import BlockQuote from './BlockQuote';
import EmbeddedAssetImage from './EmbeddedAssetImage';
import EmbeddedEntryButton from './EmbeddedEntryButton';
import EmbeddedEntryLink from './EmbeddedEntryLink';
import InlineHyperlink from './InlineHyperlink';
import EmbbedEntryTextImage from './EmbbedEntryTextImage';
import EmbbedEntryCards from './EmbeddedEntryCards';
import EmbbedEntryFaq from './EmbbedEntryFAQ';
import EmbbedEntryInfoGraphics from './EmbbedEntryInfoGraphics';

/**
 * Maps and renders content from Contentful's rich text field to React elements
 *
 * @param {{raw: string, references: ContentfulRichTextGatsbyReference[]}} document
 *
 * @returns {React.ReactElement}
 */
function RichText({ document, renderMarkOverrides, renderNodeOverrides }) {
  if (!document?.raw) return null;

  const options = {
    renderMark: {
      [MARKS.CODE]: code => {
        const content = code?.replace?.(/[“”]/g, '"');
        const isIframe = content?.indexOf('iframe') !== -1;

        if (content === '') return null;

        return <span className={isIframe ? 'rt-iframe-wrapper' : null} dangerouslySetInnerHTML={{ __html: content }} />;
      },
      ...renderMarkOverrides,
    },

    renderNode: {
      [BLOCKS.PARAGRAPH]: (_, children) => <BlockParagraph>{children}</BlockParagraph>,

      [BLOCKS.HEADING_1]: (_, children) => <BlockH1>{children}</BlockH1>,
      [BLOCKS.HEADING_2]: (_, children) => <BlockH2>{children}</BlockH2>,
      [BLOCKS.HEADING_3]: (_, children) => <BlockH3>{children}</BlockH3>,
      [BLOCKS.HEADING_4]: (_, children) => <BlockH4>{children}</BlockH4>,
      [BLOCKS.HEADING_5]: (_, children) => <BlockH5>{children}</BlockH5>,
      [BLOCKS.HEADING_6]: (_, children) => <BlockH6>{children}</BlockH6>,
      [BLOCKS.UL_LIST]: (_, children) => <BlockList component="ul">{children}</BlockList>,
      [BLOCKS.OL_LIST]: (_, children) => <BlockList component="ol">{children}</BlockList>,
      [BLOCKS.LIST_ITEM]: (_, children) => <BlockListItem>{children}</BlockListItem>,
      [BLOCKS.TABLE]: (_, children) => {
        return <BlockTable>{children}</BlockTable>;
      },

      [BLOCKS.QUOTE]: node => {
        const { content } = node.content[0];
        return <BlockQuote content={content} />;
      },

      [BLOCKS.HR]: () => <BlockHr />,

      [BLOCKS.EMBEDDED_ASSET]: node => <EmbeddedAssetImage image={node?.data?.target} />,

      [BLOCKS.EMBEDDED_ENTRY]: node => {
        const component = node?.data?.target?.__typename;
        const fields = node?.data?.target;
        switch (component) {
          case 'ContentfulElementLink':
            return fields?.isButton ? (
              <EmbeddedEntryButton
                buttonVariant={fields?.buttonVariant}
                isButton={fields?.isButton}
                color={fields?.buttonColour}
                size={fields?.buttonSize}
                noFollow={fields?.noFollow}
                url={fields?.url}
                title={fields?.title}
              />
            ) : (
              <EmbeddedEntryLink
                noFollow={fields?.noFollow}
                url={fields?.url}
                title={fields?.title}
                isButton={fields?.isButton}
                buttonVariant={fields?.buttonVariant}
              />
            );
          case 'ContentfulComponentDsTextWithImage':
            return <EmbbedEntryTextImage {...fields} />;
          case 'ContentfulComponentDsProblems':
            return <EmbbedEntryCards {...fields} />;
          case 'ContentfulComponentDsFaq':
            return <EmbbedEntryFaq {...fields} isRichText={true} />;
          case 'ContentfulComponentDsInfoGraphics':
            return <EmbbedEntryInfoGraphics {...fields} />;
          default:
            return null;
        }
      },

      [INLINES.HYPERLINK]: node => {
        const url = node?.data?.uri;
        const title = node.content ? node.content[0]?.value : node?.data?.target?.title;

        return <InlineHyperlink url={url} title={title} noFollow={node?.data?.target?.noFollow} />;
      },

      [INLINES.EMBEDDED_ENTRY]: node => {
        const component = node?.data?.target?.__typename;
        const fields = node?.data?.target;
        switch (component) {
          case 'ContentfulElementLink':
            return (
              <EmbeddedEntryLink
                url={fields?.url}
                title={fields?.title}
                isButton={fields?.isButton}
                buttonVariant={fields?.buttonVariant}
                noFollow={fields?.noFollow}
              />
            );
          default:
            return null;
        }
      },

      [INLINES.ENTRY_HYPERLINK]: node => {
        const content = node?.content;

        const title = content ? content[0]?.value : node?.data?.target?.title;
        const noFollow = node?.data?.target?.noFollow;
        const url = node?.data?.target?.url || node?.data?.target?.slug;

        const isInternal =
          typeof window !== 'undefined' && (url?.includes(window.location.host) || /^\/(?!\/)/.test(url)); // check if link is internal if window is defined

        // Ensure internal links can be opened in a new tab
        if (!isInternal)
          return (
            <a href={url} target="_blank" rel={noFollow ? 'nofollow noreferrer' : 'noopener noreferrer'}>
              {title}
            </a>
          );

        return <Link url={url}>{title}</Link>;
      },
      ...renderNodeOverrides,
    },
  };

  return renderRichText(document, options);
}

export default RichText;
